// Single registry of the /page/* SEO landing pages.
//
// Two consumers share it: the Blog index (which lists them as "tools") and
// SeoRelatedTools (the cross-link block every landing page renders). Keeping
// one list means a new landing page shows up in the internal-link graph
// automatically — the previous duplicate list in Blog.vue was the reason the
// /page/* pages only ever linked to `/` and `/blog`.
//
// `path` values carry the trailing slash on purpose: that is the canonical
// form, and the server 301s the slashless variant to it. Linking to the
// pre-redirect URL wastes a hop and splits Search Console reporting.

export const SITE_ORIGIN = 'https://steamplayti.me';

export const SEO_PAGES = [
    {
        slug: 'steam-playtime-tracker',
        shortTitle: 'Playtime Tracker',
        title: 'Steam Playtime Tracker',
        excerpt: 'Track your Steam playtime with fast, privacy-friendly charts — daily trends, most-played games, and total hours.',
        blurb: 'Daily trends, most-played games, and total hours.',
        tag: 'Tool',
        dateLabel: 'Live tool',
        path: '/page/steam-playtime-tracker/',
        cta: 'Open the tracker →',
    },
    {
        slug: 'steam-play-history',
        shortTitle: 'Play History',
        title: 'Steam Play History & Session Timeline',
        excerpt: 'See a daily timeline of every Steam session — further back than Steam\'s own two-week recently-played window.',
        blurb: 'A daily session timeline that outlives Steam\'s two-week window.',
        tag: 'Tool',
        dateLabel: 'Live tool',
        path: '/page/steam-play-history/',
        cta: 'View your history →',
    },
    {
        slug: 'steam-wasted-hours',
        shortTitle: 'Wasted Hours',
        title: 'How Many Hours Have You Wasted on Steam?',
        excerpt: 'Get a real total across your whole library, broken down by game and day.',
        blurb: 'Your lifetime total, broken down by game and day.',
        tag: 'Tool',
        dateLabel: 'Live tool',
        path: '/page/steam-wasted-hours/',
        cta: 'See your total →',
    },
    {
        slug: 'how-to-check-steam-hours',
        shortTitle: 'Check Steam Hours',
        title: 'How to Check Your Steam Hours',
        excerpt: 'Where Steam shows your hours on record — library, profile and reviews — and how to get the total and daily numbers it leaves out.',
        blurb: 'Where Steam shows your hours, and what it leaves out.',
        tag: 'Guide',
        dateLabel: 'Guide',
        path: '/page/how-to-check-steam-hours/',
        cta: 'Read the guide →',
    },
    {
        slug: 'steam-total-hours',
        shortTitle: 'Total Hours',
        title: 'Steam Total Hours',
        excerpt: 'Steam never adds up your hours. See one total across every game on your account, with your most-played games ranked.',
        blurb: 'One total across every game on your account.',
        tag: 'Tool',
        dateLabel: 'Live tool',
        path: '/page/steam-total-hours/',
        cta: 'Get your total →',
    },
    {
        slug: 'steam-hours-per-day',
        shortTitle: 'Hours Per Day',
        title: 'Steam Hours Per Day',
        excerpt: 'Daily Steam playtime per game, your average hours per day, a play calendar and playtime by month.',
        blurb: 'Daily playtime, averages per day, and a play calendar.',
        tag: 'Tool',
        dateLabel: 'Live tool',
        path: '/page/steam-hours-per-day/',
        cta: 'See your daily hours →',
    },
    {
        slug: 'steam-replay',
        shortTitle: 'Steam Replay',
        title: 'Steam Replay',
        excerpt: 'A Spotify-Wrapped-style recap of your year on Steam — top games, hours, and highlights.',
        blurb: 'A Wrapped-style recap of your year on Steam.',
        tag: 'Tool',
        dateLabel: 'Live tool',
        path: '/page/steam-replay/',
        cta: 'Get your recap →',
    },
    {
        slug: 'steam-retrospective',
        shortTitle: 'Retrospective',
        title: 'Steam Retrospective',
        excerpt: 'Look back at how your Steam habits shifted year over year.',
        blurb: 'How your Steam habits shifted year over year.',
        tag: 'Tool',
        dateLabel: 'Live tool',
        path: '/page/steam-retrospective/',
        cta: 'See your retrospective →',
    },
    {
        slug: 'steam-charts',
        shortTitle: 'Steam Charts',
        title: 'Steam Charts',
        excerpt: 'See which games and genres actually dominate your playtime, ranked.',
        blurb: 'Which games and genres actually dominate your playtime.',
        tag: 'Tool',
        dateLabel: 'Live tool',
        path: '/page/steam-charts/',
        cta: 'View the charts →',
    },
    {
        slug: 'steam-game-keywords',
        shortTitle: 'Game Keywords',
        title: 'Steam Game Keywords',
        excerpt: 'Turn your own playtime patterns into keyword and content ideas.',
        blurb: 'Turn playtime patterns into keyword and content ideas.',
        tag: 'Tool',
        dateLabel: 'Live tool',
        path: '/page/steam-game-keywords/',
        cta: 'Research keywords →',
    },
];

export function absoluteUrl (path) {
    if (!path) {
        return `${SITE_ORIGIN}/`;
    }

    return path.startsWith('http') ? path : `${SITE_ORIGIN}${path}`;
}

// Every landing page except the one being rendered. Used for the cross-link
// block, so a page never links to itself.
export function relatedPages (currentSlug, limit = 3) {
    const index = SEO_PAGES.findIndex((page) => page.slug === currentSlug);
    // Start the window just after the current page and wrap, so each page links
    // to a different slice. A fixed slice(0, n) would have every page pointing
    // at the same three, leaving the rest with no inbound in-content links.
    const ordered = index === -1
        ? SEO_PAGES.slice()
        : SEO_PAGES.slice(index + 1).concat(SEO_PAGES.slice(0, index));

    return limit > 0 ? ordered.slice(0, limit) : ordered;
}
